import 'flag-icons/css/flag-icons.min.css';
import { Button } from 'primereact/button';
import { Dropdown } from 'primereact/dropdown';
import { FileUpload } from 'primereact/fileupload';
import { InputText } from 'primereact/inputtext';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Sidebar } from 'primereact/sidebar';
import { useEffect, useRef, useState } from 'react';
import slugify from 'react-slugify';
import Tag from '../../../../components/Display/Tag';
import FormInput from '../../../../components/Form/FormInput';
import RichTextEditor from '../../../../components/Form/RichTextEditor';
import { useAccountContext } from '../../../../contexts/AccountContext';
import { useToastContext } from '../../../../contexts/ToastContext';
import { privateFetch } from '../../../../utils/apiHelper';
import { CONSTANT } from '../../../../utils/constants';
import { validateURL } from '../../../../utils/stringHelper';

function ProgramPanel({ isOpen, onClose, onCreate, program, onUpdate, loading }) {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [deezerId, setDeezerId] = useState('');
  const [videoUrl, setVideoUrl] = useState('');

  const [status, setStatus] = useState(undefined); // draft, published

  const [mainImage, setMainImage] = useState(undefined);
  const [secondaryImage, setSecondaryImage] = useState(undefined);

  const [imageLoading, setImageLoading] = useState(false);

  const { accountContext } = useAccountContext();
  const { showToast } = useToastContext();
  const mainImageFileUploadRef = useRef(null);
  const secondaryImageFileUploadRef = useRef(null);

  useEffect(() => {
    if (program) {
      setTitle(program.title);
      setStatus(program.status);
      setDeezerId(program.deezer_id);
      setVideoUrl(program.video_url);
      setDescription(program.description);
      setMainImage(program.main_image);
      setSecondaryImage(program.secondary_image);
    } else {
      setTitle('');
      setStatus(undefined);
      setDeezerId('');
      setVideoUrl('');
      setDescription('');
      setMainImage(undefined);
      setSecondaryImage(undefined);
    }
  }, [program, isOpen]);

  const uploadMainImage = async (imageForm) => {
    await privateFetch(
      'POST',
      'resources',
      `/cu/${accountContext.id}/image/`,
      "L'image a bien été ajoutée",
      imageForm,
      false,
      true
    ).then((res) => {
      setMainImage(res);
    });
    setImageLoading(false);
  };

  const uploadSecondaryImage = async (imageForm) => {
    await privateFetch(
      'POST',
      'resources',
      `/cu/${accountContext.id}/image/`,
      "L'image a bien été ajoutée",
      imageForm,
      false,
      true
    ).then((res) => {
      setSecondaryImage(res);
    });
    setImageLoading(false);
  };

  const onUploadMainImage = (event) => {
    if (event.files[0].size > CONSTANT.maxImageSize) {
      showToast('warn', '10Mo max.');
      onClearMainImage();
    } else {
      setImageLoading(true);
      const file = event.files[0];
      const imageForm = new FormData();
      imageForm.append('name', slugify(file.name));
      imageForm.append('file', file);
      uploadMainImage(imageForm);
    }
  };

  const onClearMainImage = () => {
    setMainImage(undefined);
    mainImageFileUploadRef.current.clear();
  };

  const onUploadSecondaryImage = (event) => {
    if (event.files[0].size > CONSTANT.maxImageSize) {
      showToast('warn', '10Mo max.');
      onClearSecondaryImage();
    } else {
      setImageLoading(true);
      const file = event.files[0];
      const imageForm = new FormData();
      imageForm.append('name', slugify(file.name));
      imageForm.append('file', file);
      uploadSecondaryImage(imageForm);
    }
  };

  const onClearSecondaryImage = () => {
    setSecondaryImage(undefined);
    secondaryImageFileUploadRef.current.clear();
  };

  const handleValidate = () => {
    const programPayload = {
      title,
      status,
      deezer_id: deezerId,
      video_url: videoUrl,
      description,
      main_image: mainImage?.id,
      secondary_image: secondaryImage?.id
    };
    program ? onUpdate(programPayload) : onCreate(programPayload);
  };

  const formIsValid = () => {
    return title.length > 3 && description?.length > 3 && validateURL(videoUrl);
  };

  const dropdownTemplate = (option, props) => {
    if (option) {
      return (
        <Tag
          tag={{
            label: option.label,
            bgColor: option.bgColor
          }}
          rounded
        />
      );
    }
    return <span>{props.placeholder}</span>;
  };

  const footerButtons = (
    <div className="manage-footer">
      <Button
        key={'validate'}
        label={`Valider la ${program ? 'modification' : 'création'}`}
        className="w-full"
        onClick={handleValidate}
        severity="success"
        disabled={!formIsValid()}
        loading={loading}
      />
    </div>
  );

  return (
    <Sidebar
      style={{ width: 800 }}
      visible={isOpen}
      position="right"
      onHide={() => onClose(false)}
      header={<h3>{`${program ? 'Modifier' : 'Ajouter'} un programme`}</h3>}>
      <div className="sidebar-content-container">
        <div className="form-wrapper !pb-60">
          <FormInput key="title" label={'Titre du programme'} required isValid={title.length > 3}>
            <InputText
              id="title"
              value={title}
              maxLength={300}
              placeholder="Veuillez saisir le titre du programme"
              onChange={(e) => setTitle(e.target.value)}
            />
          </FormInput>

          {program && (
            <FormInput key="status" label={'État du programme'}>
              <Dropdown
                id="status"
                value={status}
                onChange={(e) => setStatus(e.value)}
                options={CONSTANT.banquetceleste.common.statusTypes}
                optionLabel="label"
                placeholder="Veuillez sélectionner l'état de programme"
                valueTemplate={dropdownTemplate}
                itemTemplate={dropdownTemplate}
              />
            </FormInput>
          )}

          <FormInput key="deezer" label={'ID Deezer'}>
            <InputText
              id="deezerId"
              value={deezerId}
              maxLength={100}
              placeholder="Veuillez saisir l'id unique Deezer"
              onChange={(e) => setDeezerId(e.target.value)}
            />
          </FormInput>

          <FormInput
            label={'URL de la vidéo'}
            errorMessage={!validateURL(videoUrl) && "L'URL doit commencer par http:// ou https://"}>
            <InputText
              id="videoUrl"
              value={videoUrl}
              placeholder="Veuillez saisir l'url de la vidéo"
              onChange={(e) => setVideoUrl(e.target.value)}
              invalid={!validateURL(videoUrl)}
            />
          </FormInput>

          <FormInput
            key="description"
            label={'Description'}
            helpMessage={'Utiliser ctrl+maj+v pour coller le texte sans formattage'}
            required
            isValid={description?.length > 3}>
            <RichTextEditor
              value={description}
              onChange={(value) => {
                setDescription(value);
              }}
            />
          </FormInput>

          <FormInput
            key={'main-file'}
            helpMessage={'10Mo max.'}
            label={`Image principale (max. 10Mo)`}
            required
            isValid={mainImage}>
            {mainImage && (
              <img
                src={mainImage.file}
                className="w-2/3 object-cover"
                alt="illustration principale du programme"
              />
            )}
            <div className="flex flex-row gap-x-3 mt-3">
              {mainImage && (
                <Button
                  key={'delete-img'}
                  label={"Supprimer l'image"}
                  outlined
                  onClick={onClearMainImage}
                  severity="danger"
                />
              )}
              {imageLoading ? (
                <div className="w-[50px] h-[50px] p-1">
                  <ProgressSpinner style={{ width: '42px', height: '42px' }} />
                </div>
              ) : (
                <FileUpload
                  ref={mainImageFileUploadRef}
                  auto
                  disabled={mainImage}
                  mode="basic"
                  name="file"
                  accept="image/*"
                  customUpload
                  uploadHandler={onUploadMainImage}
                  chooseLabel="Depuis mon ordinateur"
                />
              )}
            </div>
          </FormInput>

          <FormInput
            key={'secondary-file'}
            helpMessage={'10Mo max.'}
            label={`Image secondaire (max. 10Mo)`}
            required
            isValid={secondaryImage}>
            {secondaryImage && (
              <img
                src={secondaryImage.file}
                className="w-2/3  object-cover"
                alt="illustration secondaire du programme"
              />
            )}
            <div className="flex flex-row gap-x-3 mt-3">
              {secondaryImage && (
                <Button
                  key={'delete-img'}
                  label={"Supprimer l'image"}
                  outlined
                  onClick={onClearSecondaryImage}
                  severity="danger"
                />
              )}
              {imageLoading ? (
                <div className="w-[50px] h-[50px] p-1">
                  <ProgressSpinner style={{ width: '42px', height: '42px' }} />
                </div>
              ) : (
                <FileUpload
                  ref={secondaryImageFileUploadRef}
                  auto
                  disabled={secondaryImage}
                  mode="basic"
                  name="file"
                  accept="image/*"
                  customUpload
                  uploadHandler={onUploadSecondaryImage}
                  chooseLabel="Depuis mon ordinateur"
                />
              )}
            </div>
          </FormInput>
        </div>
        {footerButtons}
      </div>
    </Sidebar>
  );
}

export default ProgramPanel;
